import React, { useEffect, useRef, useState } from 'react';
import { useApi } from '../../hooks';
import { formatUsd, STATUS_COLORS } from './hexMapStyles';

const isNil = (v) => v === null || v === undefined;
const formatPct = (v) => (isNil(v) ? '—' : `${Math.round(Number(v))}%`);

// Server-side shape enum (artifact contract: `concentration.shape` is
// "tight" | "diffuse") -> the short prose the design mock uses. Falls back to
// a capitalized raw value for any shape we don't recognize yet, so an
// unanticipated server value still renders something reasonable rather than
// a blank line.
const SHAPE_LABELS = {
  tight: 'Tight (single-cell)',
  diffuse: 'Diffuse (spread out)',
};
const shapeLabel = (shape) => {
  if (isNil(shape)) return null;
  if (SHAPE_LABELS[shape]) return SHAPE_LABELS[shape];
  return shape.charAt(0).toUpperCase() + shape.slice(1).replace(/_/g, ' ');
};

// artifact contract: `members[].mom_change.kind` is one of added | removed |
// tiv_change | ded_change | renewal_basis.
const MOM_BADGE_LABELS = {
  added: 'added',
  removed: 'left',
  tiv_change: 'TIV moved',
  ded_change: 'ded change',
  renewal_basis: 'renewed',
};

const statusBadgeColor = (status) => STATUS_COLORS[status] || '#8A94A6';

// Each member's TIV as a share of the cell's total TIV (per the artifact
// contract's `tiv` field on the detail payload) — 0 total tolerated (renders
// "—" rather than dividing by zero).
const memberSharePct = (memberTiv, cellTiv) => {
  if (isNil(memberTiv) || isNil(cellTiv) || Number(cellTiv) === 0) return null;
  return (Number(memberTiv) / Number(cellTiv)) * 100;
};

// CellDetailDrawer fetches on `openCell` change and guards against a stale
// response landing after a newer `openCell` has already superseded it (the
// same cancelled-flag pattern PmlSnapshotPage.jsx's effects use).
const CellDetailDrawer = ({ asOf, openCell, onClose }) => {
  const { getPmlSnapshotCellDetail } = useApi() || {};
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!openCell) {
      setDetail(null);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    setDetail(null);
    setError(null);
    (async () => {
      try {
        const res = await getPmlSnapshotCellDetail(asOf, openCell.scaleMi, openCell.cellId);
        if (cancelled) return;
        setDetail(res);
      } catch (e) {
        if (!cancelled) setError('Failed to load cell detail.');
      }
    })();
    return () => {
      cancelled = true;
    };
    // getPmlSnapshotCellDetail deliberately omitted — useApi() methods are
    // recreated on every ApiProvider re-render, and including them here would
    // risk a spurious refetch unrelated to asOf/openCell changing. Same
    // convention PmlSnapshotPage.jsx's effects use.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asOf, openCell]);

  useEffect(() => {
    if (!openCell) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onCloseRef.current && onCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [openCell]);

  if (!openCell) return null;

  return (
    <div style={S.overlay} data-testid="cell-detail-drawer">
      <div style={S.header}>
        <div style={S.headerText}>
          {detail ? (
            <>
              <span style={S.place}>{detail.place}</span>
              <span style={S.dim}> · {detail.cellId}</span>
              <span style={S.dim}> · {detail.scaleMi} mi</span>
              {detail.status && <span style={{ ...S.statusBadge, background: statusBadgeColor(detail.status) }}>{detail.status}</span>}
              <span style={S.dim}> · {formatPct(detail.utilization)} of cap</span>
            </>
          ) : (
            !error && <span style={S.dim}>{`${openCell.cellId} · ${openCell.scaleMi} mi`}</span>
          )}
        </div>
        <button type="button" aria-label="Close" onClick={() => onClose && onClose()} style={S.closeButton}>
          ×
        </button>
      </div>

      {error && <div style={S.error}>{error}</div>}

      {detail && (
        <div style={S.body}>
          <div>
            <div style={S.sectionTitle}>EP ladder</div>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Return period</th>
                  <th style={S.th}>Agg</th>
                  <th style={S.th}>Occ</th>
                </tr>
              </thead>
              <tbody>
                {(detail.epLadder || []).map((row) => (
                  <tr key={row.rp} style={row.rp === 250 ? S.trBold : undefined}>
                    <td style={S.td}>{`1-in-${row.rp}`}</td>
                    <td style={S.td}>{formatUsd(row.agg)}</td>
                    <td style={S.td}>{formatUsd(row.occ)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {detail.mom && (
            <div>
              <div style={S.sectionTitle}>Month-over-month</div>
              <ul style={S.list}>
                {(detail.mom.drivers || []).map((d, i) => (
                  <li key={i} title={d.location}>
                    {`${d.dealer} · ${MOM_BADGE_LABELS[d.kind] || d.kind} · ${formatUsd(d.delta_tiv)}`}
                    {d.location && <div style={S.driverLocation}>{d.location}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {detail.concentration && (
            <div style={S.concentrationLine}>
              {shapeLabel(detail.concentration.shape)}
              {detail.concentration.top_dealer && (
                <>
                  {' · '}
                  {detail.concentration.top_dealer}
                  {!isNil(detail.concentration.top_dealer_share_pct) && ` ${Number(detail.concentration.top_dealer_share_pct).toFixed(1)}%`}
                </>
              )}
            </div>
          )}

          <div>
            <div style={S.sectionTitle}>Members</div>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Name</th>
                  <th style={S.th}>Address</th>
                  <th style={S.th}>TIV</th>
                  <th style={S.th}>Share</th>
                  <th style={S.th}>Wind/Hail ded.</th>
                  <th style={S.th}>Flood ded.</th>
                  <th style={S.th}></th>
                </tr>
              </thead>
              <tbody>
                {(detail.members || []).map((m) => {
                  const removed = m.mom_change?.kind === 'removed';
                  return (
                    <tr key={m.location_id} style={removed ? S.trStrike : undefined}>
                      <td style={S.td}>{m.name}</td>
                      <td style={S.td}>{m.address}</td>
                      <td style={S.td}>{formatUsd(m.tiv)}</td>
                      <td style={S.td}>{formatPct(memberSharePct(m.tiv, detail.tiv))}</td>
                      <td style={S.td}>{formatUsd(m.wh_deductible)}</td>
                      <td style={S.td}>{formatUsd(m.flood_deductible)}</td>
                      <td style={S.td}>
                        {m.mom_change && <span style={S.badge}>{MOM_BADGE_LABELS[m.mom_change.kind] || m.mom_change.kind}</span>}
                        {m.mce_added && <span style={{ ...S.badge, ...S.mceBadge }}>MCE</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Inline `S` styles + a fixed-right overlay panel — same convention as
// CellMap.jsx / PmlSnapshotPage.jsx (no `src/ui` primitives here on purpose;
// see the Select/MenuItem import comment in PmlSnapshotPage.jsx for the one
// exception on this page).
const S = {
  overlay: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: 420,
    maxWidth: '90vw',
    background: '#fff',
    borderLeft: '1px solid #DFE3E8',
    boxShadow: '-2px 0 8px rgba(0,0,0,0.08)',
    padding: 16,
    overflowY: 'auto',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 },
  headerText: { fontSize: 14, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 },
  place: { fontWeight: 600 },
  dim: { color: '#637381' },
  statusBadge: { color: '#fff', borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' },
  closeButton: { border: 'none', background: 'none', fontSize: 20, lineHeight: 1, cursor: 'pointer', color: '#637381', padding: 4 },
  error: { color: '#b71c1c', fontWeight: 500, fontSize: 13 },
  body: { display: 'flex', flexDirection: 'column', gap: 16 },
  sectionTitle: { fontWeight: 600, fontSize: 13, marginBottom: 6, color: '#212B36' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: { textAlign: 'left', borderBottom: '1px solid #DFE3E8', padding: '4px 6px', color: '#637381' },
  td: { padding: '4px 6px', borderBottom: '1px solid #F4F6F8' },
  trBold: { fontWeight: 700 },
  trStrike: { textDecoration: 'line-through', color: '#919EAB' },
  list: { margin: 0, paddingLeft: 18, fontSize: 12 },
  driverLocation: { fontSize: 11, color: '#919EAB' },
  concentrationLine: { fontSize: 12, color: '#637381' },
  badge: { display: 'inline-block', marginRight: 4, padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 600, background: '#F4F6F8' },
  mceBadge: { background: '#EFE3FF', color: '#5F2994' },
};

export default CellDetailDrawer;
